import type { ComponentType } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Boxes,
  Briefcase,
  Building2,
  ClipboardList,
  Database,
  FileText,
  Gauge,
  HeartPulse,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  Mail,
  Radio,
  RefreshCw,
  Settings,
  ShieldCheck,
  Ticket,
  Upload,
  WalletCards,
} from "lucide-react";
import { matchPath } from "react-router-dom";
import type { PlatformUserRole } from "@/types";
import AccessControlPage from "@/pages/access-control/AccessControlPage";
import AnalyticsDashboard from "@/pages/analytics/AnalyticsDashboard";
import AuditPage from "@/pages/audit/AuditPage";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import SystemSettingsPage from "@/pages/governance/SystemSettingsPage";
import PlatformControlPage from "@/pages/governance/PlatformControlPage";
import ModuleCoveragePage from "@/pages/modules/ModuleCoveragePage";
import ProductGovernancePage from "@/pages/modules/ProductGovernancePage";
import AlertDashboardPage from "@/pages/monitoring/AlertDashboardPage";
import BulkOperationsPage from "@/pages/monitoring/BulkOperationsPage";
import ErrorDetailPage from "@/pages/monitoring/ErrorDetailPage";
import ErrorTrackingPage from "@/pages/monitoring/ErrorTrackingPage";
import HealthGridPage from "@/pages/monitoring/HealthGridPage";
import LifecycleDashboardPage from "@/pages/monitoring/LifecycleDashboardPage";
import LivePresencePage from "@/pages/monitoring/LivePresencePage";
import PerformanceDashboardPage from "@/pages/monitoring/PerformanceDashboardPage";
import StorageManagementPage from "@/pages/monitoring/StorageManagementPage";
import SyncHealthPage from "@/pages/monitoring/SyncHealthPage";
import CommunicationTemplatesPage from "@/pages/monitoring/CommunicationTemplatesPage";
import UsageAnalyticsPage from "@/pages/monitoring/UsageAnalyticsPage";
import VersionDashboardPage from "@/pages/monitoring/VersionDashboardPage";
import PlanListPage from "@/pages/subscriptions/PlanListPage";
import PlanModulesPage from "@/pages/subscriptions/PlanModulesPage";
import SubscriptionDetailPage from "@/pages/subscriptions/SubscriptionDetailPage";
import SubscriptionListPage from "@/pages/subscriptions/SubscriptionListPage";
import BillingOperationsPage from "@/pages/subscriptions/BillingOperationsPage";
import ImportSupportPage from "@/pages/support/ImportSupportPage";
import TicketDetailPage from "@/pages/support/TicketDetailPage";
import TicketListPage from "@/pages/support/TicketListPage";
import OnboardingImportsPage from "@/pages/tenants/OnboardingImportsPage";
import TenantDetailPage from "@/pages/tenants/TenantDetailPage";
import TenantListPage from "@/pages/tenants/TenantListPage";
import TenantOnboardingWizard from "@/pages/tenants/TenantOnboardingWizard";
import TenantControlPlanePage from "@/pages/tenants/TenantControlPlanePage";
import PartnerLandingPage from "@/pages/system/PartnerLandingPage";
import AutomationCenterPage from "@/pages/automation/AutomationCenterPage";
import AdminWorkspacePage from "@/pages/workspace/AdminWorkspacePage";
import UsageRevenuePage from "@/pages/subscriptions/UsageRevenuePage";

export type AdminNavigationGroup =
  | "Overview"
  | "Customers"
  | "Monetization"
  | "Product"
  | "Operations"
  | "Support"
  | "Governance";

export type AdminRouteDefinition = {
  path: string;
  component: ComponentType;
  title: string;
  subtitle: string;
  requiredRole?: PlatformUserRole;
  allowedRoles?: PlatformUserRole[];
  navigation?: {
    label: string;
    group: AdminNavigationGroup;
    icon: LucideIcon;
    end?: boolean;
  };
};

export const adminRouteRegistry: AdminRouteDefinition[] = [
  {
    path: "/dashboard",
    component: DashboardPage,
    title: "Overview",
    subtitle: "SaaS operations at a glance",
    requiredRole: "SUPPORT_AGENT",
    navigation: {
      label: "Overview",
      group: "Overview",
      icon: LayoutDashboard,
      end: true,
    },
  },
  {
    path: "/analytics",
    component: AnalyticsDashboard,
    title: "Analytics",
    subtitle: "Revenue, adoption and tenant health",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Analytics",
      group: "Overview",
      icon: BarChart3,
      end: true,
    },
  },
  {
    path: "/workspace",
    component: AdminWorkspacePage,
    title: "Admin workspace",
    subtitle: "Personal action center, saved views and reports",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Admin Workspace",
      group: "Overview",
      icon: FileText,
      end: true,
    },
  },
  {
    path: "/tenants",
    component: TenantListPage,
    title: "Tenants",
    subtitle: "Manage pharmacy tenants",
    requiredRole: "SUPPORT_AGENT",
    navigation: {
      label: "Tenants",
      group: "Customers",
      icon: Building2,
      end: true,
    },
  },
  {
    path: "/tenants/onboarding",
    component: TenantOnboardingWizard,
    title: "Tenant onboarding",
    subtitle: "Provision a controlled tenant activation",
    requiredRole: "SUPPORT_ADMIN",
  },
  {
    path: "/tenants/:id",
    component: TenantDetailPage,
    title: "Tenant 360",
    subtitle: "Account, operations and access controls",
    requiredRole: "SUPPORT_AGENT",
  },
  {
    path: "/tenants/:id/control-plane",
    component: TenantControlPlanePage,
    title: "Tenant control plane",
    subtitle: "Identity, ownership, compliance and data controls",
    requiredRole: "SUPPORT_AGENT",
  },
  {
    path: "/onboarding-imports",
    component: OnboardingImportsPage,
    title: "Onboarding & Imports",
    subtitle: "Controlled activation and import support",
    requiredRole: "SUPPORT_AGENT",
    navigation: {
      label: "Onboarding & Imports",
      group: "Customers",
      icon: Upload,
      end: true,
    },
  },
  {
    path: "/automation",
    component: AutomationCenterPage,
    title: "Customer success automation",
    subtitle: "Dynamic cohorts, playbooks and task operations",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Success Automation",
      group: "Customers",
      icon: ListChecks,
      end: true,
    },
  },
  {
    path: "/plans",
    component: PlanListPage,
    title: "Plans",
    subtitle: "Pricing and subscription tiers",
    requiredRole: "SUPER_ADMIN",
    navigation: {
      label: "Plans",
      group: "Monetization",
      icon: ClipboardList,
      end: true,
    },
  },
  {
    path: "/subscriptions/plans",
    component: PlanListPage,
    title: "Plans",
    subtitle: "Pricing and subscription tiers",
    requiredRole: "SUPER_ADMIN",
  },
  {
    path: "/subscriptions/plan-modules",
    component: PlanModulesPage,
    title: "Plan entitlements",
    subtitle: "Assign product capabilities to plans",
    requiredRole: "SUPER_ADMIN",
  },
  {
    path: "/subscriptions",
    component: SubscriptionListPage,
    title: "Subscriptions",
    subtitle: "Tenant plans, renewals and billing cadence",
    requiredRole: "SUPPORT_AGENT",
    navigation: {
      label: "Subscriptions",
      group: "Monetization",
      icon: WalletCards,
      end: true,
    },
  },
  {
    path: "/subscriptions/:id",
    component: SubscriptionDetailPage,
    title: "Subscription",
    subtitle: "Payments, invoices and lifecycle controls",
    requiredRole: "SUPPORT_ADMIN",
  },
  {
    path: "/billing",
    component: BillingOperationsPage,
    title: "Billing operations",
    subtitle: "Lifecycle, collections and financial controls",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Billing Operations",
      group: "Monetization",
      icon: WalletCards,
      end: true,
    },
  },
  {
    path: "/billing-automation",
    component: UsageRevenuePage,
    title: "Usage & revenue automation",
    subtitle: "Usage meters, invoice charges and revenue schedules",
    requiredRole: "SUPER_ADMIN",
    navigation: {
      label: "Usage & Revenue",
      group: "Monetization",
      icon: WalletCards,
      end: true,
    },
  },
  {
    path: "/modules",
    component: ModuleCoveragePage,
    title: "Product & Entitlements",
    subtitle: "Module catalog and policy coverage",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Modules & Entitlements",
      group: "Product",
      icon: Boxes,
      end: true,
    },
  },
  {
    path: "/modules/governance",
    component: ProductGovernancePage,
    title: "Product governance",
    subtitle: "Versioned catalog and scheduled entitlements",
    requiredRole: "SUPER_ADMIN",
    navigation: {
      label: "Product Governance",
      group: "Product",
      icon: ShieldCheck,
      end: true,
    },
  },
  {
    path: "/health",
    component: HealthGridPage,
    title: "Tenant health",
    subtitle: "Tenant and service readiness",
    requiredRole: "SUPPORT_AGENT",
    navigation: {
      label: "Health",
      group: "Operations",
      icon: HeartPulse,
      end: true,
    },
  },
  {
    path: "/monitoring/alerts",
    component: AlertDashboardPage,
    title: "Alerts",
    subtitle: "Active alerts and routing rules",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Alerts",
      group: "Operations",
      icon: AlertTriangle,
      end: true,
    },
  },
  {
    path: "/monitoring/errors",
    component: ErrorTrackingPage,
    title: "Client errors",
    subtitle: "Investigate and resolve client failures",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Errors",
      group: "Operations",
      icon: Activity,
      end: true,
    },
  },
  {
    path: "/monitoring/errors/:fingerprint",
    component: ErrorDetailPage,
    title: "Error detail",
    subtitle: "Occurrence history and resolution",
    requiredRole: "SUPPORT_ADMIN",
  },
  {
    path: "/monitoring/sync",
    component: SyncHealthPage,
    title: "Sync health",
    subtitle: "Conflicts, lag and device synchronization",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Sync",
      group: "Operations",
      icon: RefreshCw,
      end: true,
    },
  },
  {
    path: "/monitoring/storage",
    component: StorageManagementPage,
    title: "Storage",
    subtitle: "Usage, quotas and storage audits",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Storage",
      group: "Operations",
      icon: Database,
      end: true,
    },
  },
  {
    path: "/monitoring/performance",
    component: PerformanceDashboardPage,
    title: "Performance",
    subtitle: "SLA, latency and reliability",
    requiredRole: "SUPER_ADMIN",
    navigation: {
      label: "Performance",
      group: "Operations",
      icon: Gauge,
      end: true,
    },
  },
  {
    path: "/monitoring/presence",
    component: LivePresencePage,
    title: "Live presence",
    subtitle: "Connected tenant users and devices",
    requiredRole: "SUPPORT_AGENT",
    navigation: {
      label: "Live Presence",
      group: "Operations",
      icon: Radio,
      end: true,
    },
  },
  {
    path: "/monitoring/lifecycle",
    component: LifecycleDashboardPage,
    title: "Tenant lifecycle",
    subtitle: "Segments, adoption and account health",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Lifecycle",
      group: "Operations",
      icon: ListChecks,
      end: true,
    },
  },
  {
    path: "/monitoring/usage",
    component: UsageAnalyticsPage,
    title: "Usage analytics",
    subtitle: "Product adoption and utilization",
    requiredRole: "SUPER_ADMIN",
    navigation: {
      label: "Usage",
      group: "Operations",
      icon: BarChart3,
      end: true,
    },
  },
  {
    path: "/monitoring/versions",
    component: VersionDashboardPage,
    title: "Client versions",
    subtitle: "Compatibility and rollout readiness",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Versions",
      group: "Operations",
      icon: RefreshCw,
      end: true,
    },
  },
  {
    path: "/monitoring/bulk-operations",
    component: BulkOperationsPage,
    title: "Bulk operations",
    subtitle: "Controlled asynchronous tenant actions",
    requiredRole: "SUPER_ADMIN",
    navigation: {
      label: "Bulk Operations",
      group: "Operations",
      icon: ListChecks,
      end: true,
    },
  },
  {
    path: "/monitoring/communications",
    component: CommunicationTemplatesPage,
    title: "Communications",
    subtitle: "Reusable customer communication templates",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Communications",
      group: "Operations",
      icon: Mail,
      end: true,
    },
  },
  {
    path: "/support",
    component: TicketListPage,
    title: "Support",
    subtitle: "Tickets and escalations",
    requiredRole: "SUPPORT_AGENT",
    navigation: {
      label: "Tickets",
      group: "Support",
      icon: LifeBuoy,
      end: true,
    },
  },
  {
    path: "/support/:id",
    component: TicketDetailPage,
    title: "Support ticket",
    subtitle: "Investigation and customer response",
    requiredRole: "SUPPORT_AGENT",
  },
  {
    path: "/support/imports",
    component: ImportSupportPage,
    title: "Import Support",
    subtitle: "Controlled store-scoped diagnostics",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Import Support",
      group: "Support",
      icon: Ticket,
      end: true,
    },
  },
  {
    path: "/audit",
    component: AuditPage,
    title: "Audit Log",
    subtitle: "Administrative activity history",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Audit",
      group: "Governance",
      icon: FileText,
      end: true,
    },
  },
  {
    path: "/access-control",
    component: AccessControlPage,
    title: "Access Control",
    subtitle: "Platform identities, roles and permissions",
    requiredRole: "SUPER_ADMIN",
    navigation: {
      label: "Access Control",
      group: "Governance",
      icon: ShieldCheck,
      end: true,
    },
  },
  {
    path: "/security-controls",
    component: PlatformControlPage,
    title: "Security & approvals",
    subtitle: "Scoped access, maker-checker and identity controls",
    requiredRole: "SUPPORT_ADMIN",
    navigation: {
      label: "Security & Approvals",
      group: "Governance",
      icon: ShieldCheck,
      end: true,
    },
  },
  {
    path: "/settings",
    component: SystemSettingsPage,
    title: "Settings",
    subtitle: "Platform configuration",
    requiredRole: "SUPER_ADMIN",
    navigation: {
      label: "Settings",
      group: "Governance",
      icon: Settings,
      end: true,
    },
  },
  {
    path: "/partner",
    component: PartnerLandingPage,
    title: "Partner Console",
    subtitle: "Scoped channel-partner access",
    allowedRoles: ["CHANNEL_PARTNER"],
    navigation: {
      label: "Partner Console",
      group: "Overview",
      icon: Briefcase,
      end: true,
    },
  },
];

export const adminNavigationGroups: AdminNavigationGroup[] = [
  "Overview",
  "Customers",
  "Monetization",
  "Product",
  "Operations",
  "Support",
  "Governance",
];

export function findAdminRoute(pathname: string) {
  return adminRouteRegistry.find((route) =>
    matchPath({ path: route.path, end: true }, pathname),
  );
}

export function getDefaultAdminRoute(role?: PlatformUserRole | null) {
  return role === "CHANNEL_PARTNER" ? "/partner" : "/dashboard";
}

export function canAccessAdminRoute(
  route: AdminRouteDefinition,
  role: PlatformUserRole | null,
  hasRole: (requiredRole: PlatformUserRole) => boolean,
) {
  if (!role) return false;
  if (route.allowedRoles) return route.allowedRoles.includes(role);
  return route.requiredRole ? hasRole(route.requiredRole) : true;
}
